import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Typography,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import remitoCompraService, { RemitoCompra } from '../../services/remitoCompraService';
import facturaCompraService, { FacturaCompra } from '../../services/facturaCompraService';
import ubicacionService from '../../services/ubicacionService';
import { formatDate } from '../../utils/formatters';

const formatearFecha = (fecha: Date | string | null | undefined): string => {
  if (!fecha) return '-';
  const d = typeof fecha === 'string' ? new Date(fecha) : fecha;
  if (isNaN(d.getTime())) return '-';
  return formatDate(d.toISOString().split('T')[0]);
};

/** Formato Letra-Suc-Nro: A-0001-00000001 */
const formatoLetraSucNro = (letra?: string, nroSuc?: number, nroComprobante?: number | string): string => {
  const L = (letra ?? '').trim() || '-';
  const suc = String(nroSuc ?? 0).padStart(4, '0');
  const nro =
    typeof nroComprobante === 'number'
      ? nroComprobante
      : parseInt(nroComprobante ?? '', 10);
  const safeNro = Number.isNaN(nro) ? 0 : nro;
  return `${L}-${suc}-${String(safeNro).padStart(8, '0')}`;
};

const RemitoCompraDetalle: React.FC = () => {
  const navigate = useNavigate();
  const { nroFormulario } = useParams<{ nroFormulario: string }>();
  const { enqueueSnackbar } = useSnackbar();

  const [remito, setRemito] = useState<RemitoCompra | null>(null);
  const [facturaAsociada, setFacturaAsociada] = useState<FacturaCompra | null>(null);
  const [ubicacionDestinoNombre, setUbicacionDestinoNombre] = useState<string>('');
  const [loading, setLoading] = useState(true);

  const loadRemito = useCallback(async () => {
    const id = parseInt(nroFormulario || '', 10);
    if (Number.isNaN(id)) {
      enqueueSnackbar('Remito no válido', { variant: 'error' });
      navigate('/compras/remitos');
      return;
    }

    try {
      setLoading(true);
      const data = await remitoCompraService.getRemitoCompra(id);
      setRemito(data);
      setFacturaAsociada(null);

      if (data?.nroFormularioFactura && data.nroFormularioFactura > 0) {
        try {
          const fc = await facturaCompraService.getFacturaCompra(data.nroFormularioFactura);
          setFacturaAsociada(fc);
        } catch (e) {
          console.warn('No se pudo cargar la factura asociada:', e);
          setFacturaAsociada(null);
        }
      }

      if (data?.nroUbicacionDestino) {
        try {
          const u = await ubicacionService.getUbicacion(data.nroUbicacionDestino);
          setUbicacionDestinoNombre(u?.nombreUbicacion?.trim() || '');
        } catch {
          setUbicacionDestinoNombre('');
        }
      } else {
        setUbicacionDestinoNombre('');
      }
    } catch (error) {
      console.error('Error al cargar remito:', error);
      enqueueSnackbar('Error al cargar el remito de compra', { variant: 'error' });
      navigate('/compras/remitos');
    } finally {
      setLoading(false);
    }
  }, [nroFormulario, enqueueSnackbar, navigate]);

  useEffect(() => {
    loadRemito();
  }, [loadRemito]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress sx={{ color: '#1976d2' }} />
      </Box>
    );
  }

  if (!remito) return null;

  const nroComprobanteTxt = remito.nroComprobante ?? remito.nroRemito ?? '';
  const tieneFacturaAsociada = !!remito.nroFormularioFactura && (remito.nroFormularioFactura as number) > 0;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() =>
                window.history.length > 1 ? navigate(-1) : navigate('/compras/remitos')
              }
              sx={{ color: '#1976d2' }}
            >
              Volver
            </Button>
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
              Remito de Compra {formatoLetraSucNro(remito.letra, remito.nroSuc, nroComprobanteTxt)}
            </Typography>
          </Stack>

          <Typography variant="h6" sx={{ mb: 2, color: '#1976d2' }}>
            Encabezado
          </Typography>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                Nro. Formulario
              </Typography>
              <Typography variant="body1">{remito.nroFormulario}</Typography>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                Fecha emisión
              </Typography>
              <Typography variant="body1">{formatearFecha(remito.fechaEmision)}</Typography>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                Nro Comprobante
              </Typography>
              <Typography variant="body1">{formatoLetraSucNro(remito.letra, remito.nroSuc, nroComprobanteTxt)}</Typography>
            </Grid>

            {tieneFacturaAsociada && (
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Comprobante factura
                </Typography>
                <Typography variant="body1">
                  {facturaAsociada
                    ? formatoLetraSucNro(
                        facturaAsociada.letra,
                        facturaAsociada.nroSuc,
                        facturaAsociada.nroComprobante ?? undefined
                      )
                    : '-'}
                </Typography>
              </Grid>
            )}

            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                Proveedor
              </Typography>
              <Typography variant="body1">
                {remito.nroCliente != null ? remito.nroCliente : '-'} - {remito.nombre || '-'}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                Ubicación destino
              </Typography>
              <Typography variant="body1">
                {remito.nroUbicacionDestino != null
                  ? ubicacionDestinoNombre
                    ? `${remito.nroUbicacionDestino} - ${ubicacionDestinoNombre}`
                    : String(remito.nroUbicacionDestino)
                  : '-'}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                Usuario
              </Typography>
              <Typography variant="body1">{remito.usuario || '-'}</Typography>
            </Grid>

            {remito.descripcion && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Descripción
                </Typography>
                <Typography variant="body1">{remito.descripcion}</Typography>
              </Grid>
            )}
          </Grid>

          <Typography variant="h6" sx={{ mb: 2, color: '#1976d2' }}>
            Detalle
          </Typography>

          <TableContainer sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Código</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Descripción</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    Cantidad
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Notas</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {(remito.detalles || []).map((d, idx) => (
                  <TableRow key={`${d.nroIdformulario}-${d.nroItem}-${idx}`}>
                    <TableCell>{d.codigoArticulo || '-'}</TableCell>
                    <TableCell>{d.descripcionArticulo || '-'}</TableCell>
                    <TableCell align="right">
                      {Number(d.cantidad)?.toLocaleString('es-AR', { minimumFractionDigits: 2 }) ?? '-'}
                    </TableCell>
                    <TableCell>{d.notas || ''}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default RemitoCompraDetalle;

